import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { useAuthStore, type ProvisionOutcome } from '../store/auth.ts';

function provLabel(outcome: ProvisionOutcome): string {
  if (outcome === 'failed') return 'could not be created (you can still sign in to Office)';
  return outcome === 'exists' ? 'account already existed' : 'account created';
}

export function AcceptInvite(): JSX.Element {
  const navigate = useNavigate();
  const { token } = useParams({ from: '/invite/$token' });
  const reg = useAuthStore((s) => s.registerViaInvite);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [provisioning, setProvisioning] = useState<
    { wapsign?: ProvisionOutcome; wapmeet?: ProvisionOutcome; wapstat?: ProvisionOutcome } | null
  >(null);

  if (provisioning) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12">
        <h1 className="text-3xl font-normal mb-2 text-ink">You&rsquo;re all set</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Your account is ready. Taking you to your dashboard…
        </p>
        <ul className="space-y-2">
          {provisioning.wapsign && (
            <li className="flex items-center gap-2 text-sm">
              <Icon name="check_circle" size={18} className="text-[hsl(var(--success))]" />
              <span className="text-foreground">WapSign</span>
              <span className="text-muted-foreground">— {provLabel(provisioning.wapsign)}</span>
            </li>
          )}
          {provisioning.wapmeet && (
            <li className="flex items-center gap-2 text-sm">
              <Icon name="check_circle" size={18} className="text-[hsl(var(--success))]" />
              <span className="text-foreground">WapMeet</span>
              <span className="text-muted-foreground">— {provLabel(provisioning.wapmeet)}</span>
            </li>
          )}
          {provisioning.wapstat && (
            <li className="flex items-center gap-2 text-sm">
              <Icon name="check_circle" size={18} className="text-[hsl(var(--success))]" />
              <span className="text-foreground">WapStat</span>
              <span className="text-muted-foreground">— {provLabel(provisioning.wapstat)}</span>
            </li>
          )}
        </ul>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-12">
      <h1 className="text-3xl font-normal mb-2 text-ink">Accept your invite</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Set up your Wapplify Office account to join your organization.
      </p>
      <form
        className="space-y-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          try {
            const prov = await reg(token, password, name);
            if (prov && (prov.wapsign || prov.wapmeet || prov.wapstat)) {
              // Briefly show what was provisioned before redirecting.
              setProvisioning(prov);
              setTimeout(() => void navigate({ to: '/dashboard' }), 2200);
            } else {
              await navigate({ to: '/dashboard' });
            }
          } catch {
            setError('Invite is invalid or expired.');
          }
        }}
      >
        <input
          className="input"
          placeholder="Your name"
          aria-label="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <input
          className="input"
          type="password"
          placeholder="Choose a password (≥ 8 chars)"
          aria-label="Password (≥ 8 chars)"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          className="btn-primary w-full"
        >
          Create account
        </button>
      </form>
    </div>
  );
}
